/** Base API client: CSRF-proofed writes, error envelopes, and session refusals. */

import { csrfToken, withCsrf } from "@/lib/csrf";
import { markAccessDenied, refusalIn } from "@/lib/access-denied";
import {
  heldASession,
  isSessionRefusal,
  markSessionExpired,
} from "@/lib/session-expiry";
import { errorWords } from "@/lib/problem/error-words";
import {
  TIMED_OUT_WORDS,
  UPLOAD_TIMEOUT_MS,
  WRITE_TIMED_OUT,
  WRITE_TIMEOUT_MS,
  underDeadline,
} from "./write-deadline";

/**
 * Origin-relative, and not configurable.
 *
 * Identity is a same-origin cookie now, so an absolute base pointing at another
 * origin cannot carry it: `apiFetch` sends `credentials: "same-origin"`, every
 * call 401s, and the session is marked over moments after a successful sign-in
 * while `/api/auth/me` on this origin still reports the user as signed in. The
 * override that allowed it is gone rather than left as a setting whose only
 * documented value breaks the app.
 */
export const BASE_URL = "/api";

export class ApiError extends Error {
  /** Words the attorney may read as they are: a validation sentence, or a refusal the API wrote for them. */
  readonly words: string | null;
  /** The `X-Request-ID` the API answered with, which finds the request's log lines. */
  readonly requestId: string | null;

  constructor(
    public status: number,
    /** The server's own words, for the log: they can name ids, so they are never shown. */
    public detail: string,
    /** The domain envelope's code, where the server sent one: what kind of refusal it is. */
    public code: string | null = null,
    {
      words = null,
      requestId = null,
    }: { words?: string | null; requestId?: string | null } = {},
  ) {
    super(`API error ${status}: ${detail}`);
    this.name = "ApiError";
    this.words = words;
    this.requestId = requestId;
  }
}

/**
 * Why the server refused, in words the attorney can read: the API's own only where it wrote
 * them for the attorney, otherwise the web's sentence for the refusal's code. Null where there is
 * nothing to add (a fault on our side, or a failure that never reached the server), for a caller
 * whose own sentence reads whole without it.
 */
export function refusalWords(error: unknown): string | null {
  return error instanceof ApiError
    ? (error.words ?? errorWords(error.code))
    : null;
}

/**
 * The words a refusal is shown with: why the server refused, or the caller's
 * own sentence where there is nothing to add.
 */
export function refusalDetail(error: unknown, fallback: string): string {
  return refusalWords(error) ?? fallback;
}

/**
 * Build the headers for a request.
 *
 * No Authorization header: the session is a cookie the browser attaches
 * itself, and this app never holds a token. What a write does need is the CSRF
 * proof, because a cookie alone cannot show the request came from us.
 */
export function authHeaders(
  extra?: Record<string, string>,
  method?: string,
): Record<string, string> {
  return withCsrf(method, extra);
}

/** What to change, for an entry of FastAPI's request-validation list, without its internal field names. */
function validationSentence(item: unknown): string {
  if (typeof item !== "object" || item === null) return "";
  const { type, ctx } = item as { type?: unknown; ctx?: unknown };
  const limits = (typeof ctx === "object" && ctx !== null ? ctx : {}) as {
    max_length?: unknown;
    min_length?: unknown;
  };
  const max = typeof limits.max_length === "number" ? limits.max_length : null;
  const min = typeof limits.min_length === "number" ? limits.min_length : null;
  if (type === "string_too_long" && max !== null) {
    return `This text is too long. Use ${String(max)} characters or fewer.`;
  }
  if (type === "string_too_short") {
    return min === null || min <= 1
      ? "This text cannot be empty."
      : `This text is too short. Use ${String(min)} characters or more.`;
  }
  if (type === "too_long" && max !== null) {
    return `There are too many items. Use ${String(max)} or fewer.`;
  }
  return "The change could not be saved as it is. Check what you entered and try again.";
}

/** What a failed response's body says, read once. */
interface ErrorBodyReading {
  /** The domain envelope's code, or null for any other body. */
  code: string | null;
  /** The server's own sentence, for the log, or null if it has none. */
  detail: string | null;
  /** What the attorney may read as it is: a validation sentence, or a message the API marked for them. */
  words: string | null;
}

/**
 * Read an error body. Three shapes arrive in practice: the API's domain envelope
 * (`{"error": {"code", "message", "details", "for_attorney"}}`, rendered by the APIError
 * handler), FastAPI's `{"detail": "…"}` for a plain HTTPException, and FastAPI's
 * `{"detail": [{type, loc, msg, ctx}]}` for a request-validation failure, which is said in
 * plain words rather than in the server's field names.
 */
function readErrorBody(body: unknown): ErrorBodyReading {
  const { error, detail } = (
    typeof body === "object" && body !== null ? body : {}
  ) as { error?: unknown; detail?: unknown };
  const envelope = (
    typeof error === "object" && error !== null ? error : {}
  ) as {
    code?: unknown;
    message?: unknown;
    for_attorney?: unknown;
  };
  const message =
    typeof envelope.message === "string" && envelope.message.trim()
      ? envelope.message
      : null;
  // One sentence per kind of fault: five lines too long are one thing to fix.
  const sentences = Array.isArray(detail)
    ? [...new Set(detail.map(validationSentence).filter(Boolean))]
    : [];
  const validation = sentences.length > 0 ? sentences.join(" ") : null;
  return {
    code: typeof envelope.code === "string" ? envelope.code : null,
    detail:
      message ??
      (typeof detail === "string" && detail.trim() ? detail : validation),
    words: validation ?? (envelope.for_attorney === true ? message : null),
  };
}

async function throwIfNotOk(response: Response): Promise<void> {
  if (!response.ok) {
    // Noticed before the body is read, because a 401 carries no envelope worth
    // parsing and the answer does not depend on one. Every failed response
    // passes through here, which is the only place that sees them all.
    //
    // Gated on the browser holding a CSRF cookie, which is the one half of the
    // pair it can read. The API sets both at the same moment with the same
    // lifetime and clears both on sign-out, so its presence is this side's only
    // evidence that there was a session to lose. Without the gate, the ordinary
    // 401 that answers a signed-out visitor at `/auth/me` would report a
    // session ending to somebody who never had one.
    if (
      isSessionRefusal(response.status) &&
      heldASession(Boolean(csrfToken()))
    ) {
      markSessionExpired();
    }

    let detail: string | null = null;
    let code: string | null = null;
    let words: string | null = null;
    try {
      const body: unknown = await response.json();
      // Noticed here because this is the one place every failed response
      // passes through. The refusal arrives on an ordinary call rather than
      // at sign-in, so there is no other moment to catch it.
      const refusal = refusalIn(response.status, body);
      if (refusal) markAccessDenied(refusal);
      ({ code, detail, words } = readErrorBody(body));
    } catch {
      detail = null;
    }
    // No body to read: the HTTP reason phrase is the next best sentence, and
    // over HTTP/2 there is not even one, so end on something an attorney can
    // report rather than an empty red box.
    detail ??=
      response.statusText.trim() ||
      `Request failed with status ${String(response.status)}.`;
    throw new ApiError(response.status, detail, code, {
      words,
      requestId: response.headers.get("X-Request-ID"),
    });
  }
}

async function handleResponse<T>(response: Response): Promise<T> {
  await throwIfNotOk(response);
  return response.json() as Promise<T>;
}

/**
 * Execute a fetch request against the API.
 *
 * An expired access token is refreshed by the API underneath the request, so a
 * 401 reaching here means the session itself has gone, and retrying would only
 * ask the same question twice. What happens instead is in
 * `lib/session-expiry.ts`: the app returns to sign-in once, rather than every
 * query rendering its own error box forever. The one retry is for a renewal
 * that failed for now (a 503 with its own code): asked once more after a
 * moment, and never read as the session ending.
 *
 * `credentials: "same-origin"` is explicit rather than relied upon: it is the
 * fetch default, but it is now the only thing carrying identity, and a default
 * that silently changed would log everyone out.
 */
export async function apiFetch(
  input: string,
  init?: RequestInit,
): Promise<Response> {
  const send = () => fetch(input, { ...init, credentials: "same-origin" });
  const response = await send();
  if (!(await isRenewalUnavailable(response))) return response;
  // The API could not renew the session's token just now, and said so before
  // the route ran, so asking again repeats nothing. A deploy or a slow answer
  // from the identity provider passes in moments; the session itself stands.
  await new Promise((resolve) => setTimeout(resolve, RENEWAL_RETRY_MS));
  return send();
}

/** The API's code for a renewal that failed for now: a 503, never a 401. */
const RENEWAL_UNAVAILABLE = "SESSION_RENEWAL_UNAVAILABLE";
/** How long to wait before asking once more after a renewal that failed for now. */
const RENEWAL_RETRY_MS = 1000;

/** Whether this is the API saying the session's token could not be renewed just now. */
async function isRenewalUnavailable(response: Response): Promise<boolean> {
  if (response.status !== 503) return false;
  try {
    return (
      readErrorBody(await response.clone().json()).code === RENEWAL_UNAVAILABLE
    );
  } catch {
    return false;
  }
}

/**
 * Make a GET request to the API.
 *
 * `signal` is accepted so a caller that outlives its own request can cancel it
 * rather than letting it finish and discarding the answer.
 */
export async function get<T>(
  path: string,
  init?: { signal?: AbortSignal },
): Promise<T> {
  const response = await apiFetch(`${BASE_URL}${path}`, {
    headers: authHeaders(),
    signal: init?.signal,
  });
  return handleResponse<T>(response);
}

/** A GET whose status is the whole answer: success carries no body, and a refusal throws as any other. */
export async function getNoContent(path: string): Promise<void> {
  const response = await apiFetch(`${BASE_URL}${path}`, {
    headers: authHeaders(),
  });
  await throwIfNotOk(response);
}

/** How long a write may wait for its answer, where it is not the usual deadline. */
export interface WriteOptions {
  deadlineMs?: number;
}

/**
 * Send one write and read its answer under its deadline; one the server does not answer in
 * time, headers and body, is refused.
 */
function write<T>(
  path: string,
  init: RequestInit,
  read: (response: Response) => Promise<T>,
  { deadlineMs = WRITE_TIMEOUT_MS }: WriteOptions = {},
): Promise<T> {
  return underDeadline(
    async (signal) =>
      read(await apiFetch(`${BASE_URL}${path}`, { ...init, signal })),
    deadlineMs,
    () =>
      new ApiError(0, TIMED_OUT_WORDS, WRITE_TIMED_OUT, {
        words: TIMED_OUT_WORDS,
      }),
  );
}

const JSON_HEADERS = { "Content-Type": "application/json" };

/** Make a POST request with a JSON body. */
export function post<T>(
  path: string,
  body?: unknown,
  options?: WriteOptions,
): Promise<T> {
  return write(
    path,
    {
      method: "POST",
      headers: authHeaders(JSON_HEADERS, "POST"),
      body: body !== undefined ? JSON.stringify(body) : undefined,
    },
    handleResponse<T>,
    options,
  );
}

/** Make a PATCH request with a JSON body. */
export function patch<T>(path: string, body: unknown): Promise<T> {
  return write(
    path,
    {
      method: "PATCH",
      headers: authHeaders(JSON_HEADERS, "PATCH"),
      body: JSON.stringify(body),
    },
    handleResponse<T>,
  );
}

/** Make a PUT request with a JSON body. */
export function put<T>(path: string, body: unknown): Promise<T> {
  return write(
    path,
    {
      method: "PUT",
      headers: authHeaders(JSON_HEADERS, "PUT"),
      body: JSON.stringify(body),
    },
    handleResponse<T>,
  );
}

/** A DELETE whose reply carries a body: what the removal left behind. */
export function delReturning<T>(path: string): Promise<T> {
  return write(
    path,
    { method: "DELETE", headers: authHeaders(undefined, "DELETE") },
    handleResponse<T>,
  );
}

/** Make a DELETE request whose reply carries nothing. */
export function del(path: string, options?: WriteOptions): Promise<void> {
  return write(
    path,
    { method: "DELETE", headers: authHeaders(undefined, "DELETE") },
    throwIfNotOk,
    options,
  );
}

/**
 * Upload a file via multipart/form-data, POST unless the route replaces what
 * it holds, as a client's one logo does.
 */
export async function upload<T>(
  path: string,
  file: File,
  fields?: Record<string, string>,
  method: "POST" | "PUT" = "POST",
): Promise<T> {
  const formData = new FormData();
  formData.append("file", file);
  if (fields) {
    for (const [key, value] of Object.entries(fields)) {
      formData.append(key, value);
    }
  }
  // Don't set Content-Type — browser sets multipart boundary automatically
  return write(
    path,
    { method, headers: authHeaders(undefined, method), body: formData },
    handleResponse<T>,
    { deadlineMs: UPLOAD_TIMEOUT_MS },
  );
}
